import { useEffect, useState } from 'react';
import { useSettingsStore, DaySchedule } from '../../store/settingsStore';
import { useEstablishmentStore } from '../../store/establishmentStore';
import { logger } from '../../utils/logger';
import { DEFAULT_BRAND_COLORS } from '../../constants/brandColors';
import { uniqueEntriesByCalendarDay } from '../../utils/calendarDate';

export const useEstablishmentData = () => {
  const {
    branding,
    socialMedia,
    setBranding,
    setSocialMedia,
    updateDaySchedule,
    setSpecialSchedules,
    setExceptionDates,
    isDataLoaded,
    markDataAsLoaded,
  } = useSettingsStore();

  const { establishment } = useEstablishmentStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadEstablishmentData = async () => {
      // Only load from Zustand establishment if NOT loaded before
      if (isDataLoaded || !establishment) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Update Zustand settings with establishment data
        if (
          establishment.logo ||
          establishment.slogan ||
          establishment.primaryColor ||
          establishment.secondaryColor
        ) {
          setBranding({
            logo: establishment.logo || '',
            slogan: establishment.slogan || '',
            primaryColor: establishment.primaryColor || DEFAULT_BRAND_COLORS.primary,
            secondaryColor: establishment.secondaryColor || DEFAULT_BRAND_COLORS.secondary,
          });
        }

        // Also save social media if it exists
        if (establishment.socialMedia) {
          setSocialMedia({
            facebook: establishment.socialMedia.facebook || '',
            instagram: establishment.socialMedia.instagram || '',
            whatsapp: (establishment.socialMedia as any).whatsapp || '',
            twitter: (establishment.socialMedia as any).twitter || '',
            linkedin: (establishment.socialMedia as any).linkedin || '',
            tiktok: establishment.socialMedia.tiktok || '',
            youtube: (establishment.socialMedia as any).youtube || '',
            website: establishment.socialMedia.website || '',
          });
        }

        // Carregar horários regulares
        if (establishment.weeklyOperatingHours) {
          const days: DaySchedule['day'][] = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
          ];

          days.forEach((day) => {
            const dayHours = establishment.weeklyOperatingHours?.[day];
            if (dayHours) {
              updateDaySchedule(day, {
                enabled: true,
                openTime: dayHours.open,
                closeTime: dayHours.close,
              });
            }
          });
        }

        // Carregar horários especiais
        if (establishment.specialOperatingDays && establishment.specialOperatingDays.length > 0) {
          const mappedSpecialSchedules = establishment.specialOperatingDays.map((special: any) => ({
            id: crypto.randomUUID(),
            date: special.date,
            openTime: special.open,
            closeTime: special.close,
          }));
          setSpecialSchedules(mappedSpecialSchedules);
        } else {
          setSpecialSchedules([]);
        }

        // Carregar datas de exceção
        if (establishment.closedDays && establishment.closedDays.length > 0) {
          const mappedExceptionDates = uniqueEntriesByCalendarDay(
            establishment.closedDays.map((closed) => ({
              id: crypto.randomUUID(),
              date: String(closed.date),
              reason: closed.reason || '',
            }))
          );
          setExceptionDates(mappedExceptionDates);
        } else {
          setExceptionDates([]);
        }

        // Mark as loaded
        markDataAsLoaded();
      } catch (err) {
        const errorMsg = 'Erro ao carregar configuração';
        setError(errorMsg);
        logger.error(errorMsg, err);
      } finally {
        setIsLoading(false);
      }
    };

    loadEstablishmentData();
  }, [
    establishment,
    isDataLoaded,
    markDataAsLoaded,
    setBranding,
    setSocialMedia,
    updateDaySchedule,
    setSpecialSchedules,
    setExceptionDates,
  ]);

  return {
    branding,
    socialMedia,
    isLoading,
    error,
    isDataLoaded,
  };
};
